
import React from 'react';
import { ReactTyped } from "react-typed";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from 'react-scroll';

interface HeroSectionProps {
  profileImage: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ profileImage }) => {
  return (
    <div className="relative min-h-[85vh] flex items-center">
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-transparent"></div>
      
      <div className="container mx-auto px-4 z-10">
        <motion.div 
          className="grid md:grid-cols-2 gap-8 items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="order-2 md:order-1">
            <motion.h4 
              className="text-yellow-300 uppercase tracking-wider mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Welcome
            </motion.h4>
            
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Hi, I'm <span className="text-yellow-300">
                <ReactTyped
                  strings={['Qadeer Ahmad', 'a Software Developer', 'a Problem Solver']}
                  typeSpeed={40}
                  backSpeed={50}
                  loop
                />
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl text-gray-300 mb-8 max-w-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              A software developer specializing in creating innovative, scalable systems and modern web applications.
            </motion.p>
            
            <motion.div 
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <Link to="projects" smooth={true} duration={500}>
                <Button className="bg-yellow-300 text-black hover:bg-yellow-400 px-6 py-2 rounded-md flex items-center gap-2 text-base">
                  View Projects 
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="contact" smooth={true} duration={500}>
                <Button variant="outline" className="border-yellow-300 text-yellow-300 hover:bg-yellow-300/10 px-6 py-2 rounded-md">
                  Contact Me
                </Button>
              </Link>
            </motion.div>
          </div>
          
          <div className="order-1 md:order-2 flex justify-center">
            <motion.div 
              className="relative"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.6,
                delay: 0.5,
              }}
            >
              <div className="w-64 h-64 md:w-80 md:h-80 overflow-hidden rounded-full border-4 border-yellow-300 shadow-xl shadow-yellow-300/20">
                <img 
                  src={profileImage} 
                  alt="Qadeer Ahmad" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -z-10 inset-0 bg-yellow-300/20 blur-3xl rounded-full transform scale-90"></div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HeroSection;
